import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The bell's chime, for a notification that arrives while the app is open
 * (operator's request, 2026-10-04). Two rising notes drawn with the Web Audio
 * API, so there is no sound file to ship or cache.
 *
 * It sounds once per notification however many tabs are open, never for what
 * was already waiting when the page opened, and not at all once someone mutes
 * it from the bell. That choice is kept per browser, like the theme.
 */

const MUTED_KEY = 'viganium.notificationSound'; // 'off' = muted
const HEARD_KEY = 'viganium.notificationHeardAt'; // the newest notification any tab has chimed for

/** A5, then E6 a fifth above, each with a softer octave that fades first. */
const NOTES = [
  { hz: 880, at: 0, length: 0.6 },
  { hz: 1318.51, at: 0.13, length: 1 },
];
const VOLUME = 0.3;

export function soundOn(): boolean {
  return localStorage.getItem(MUTED_KEY) !== 'off';
}

let context: AudioContext | null = null;
let quietAt = 0; // performance.now() once the last chime has rung out
let releaseTimer = 0;

/**
 * Until someone has clicked or typed in the page the browser keeps it silent;
 * a context made then would only be refused, with a console warning.
 */
function mayPlay(): boolean {
  if (typeof AudioContext === 'undefined') return false;
  return !navigator.userActivation || navigator.userActivation.hasBeenActive;
}

function audio(): AudioContext | null {
  if (!context && mayPlay()) context = new AudioContext();
  return context;
}

/** Lets go of the sound device once the last chime has rung out. */
function release(ctx: AudioContext) {
  window.clearTimeout(releaseTimer);
  releaseTimer = window.setTimeout(
    () => void ctx.suspend().catch(() => undefined),
    Math.max(0, quietAt - performance.now()) + 300,
  );
}

export async function playChime(): Promise<void> {
  const ctx = audio();
  if (!ctx) return;
  window.clearTimeout(releaseTimer);
  if (ctx.state === 'suspended') {
    // Opening the sound device again takes a moment (~300 ms on Windows). When
    // the page may not make sound at all, resume() never settles: give up after
    // a while, and still let go of the device should it start later.
    const resumed = ctx.resume();
    const started = await Promise.race([
      resumed.then(() => true, () => false),
      new Promise<boolean>((resolve) => window.setTimeout(() => resolve(false), 3000)),
    ]);
    if (!started) {
      void resumed.then(() => release(ctx), () => undefined);
      return;
    }
  }
  if (ctx.state !== 'running') return;

  const start = ctx.currentTime + 0.02;
  const out = ctx.createGain();
  out.gain.value = VOLUME;
  out.connect(ctx.destination);
  for (const note of NOTES) {
    tone(ctx, out, note.hz, start + note.at, note.length, 1);
    tone(ctx, out, note.hz * 2, start + note.at, note.length / 2, 0.2);
  }
  const end = Math.max(...NOTES.map((note) => note.at + note.length));
  quietAt = Math.max(quietAt, performance.now() + (0.02 + end) * 1000);
  release(ctx);
}

function tone(ctx: AudioContext, out: AudioNode, hz: number, at: number, length: number, peak: number) {
  const oscillator = ctx.createOscillator();
  oscillator.frequency.value = hz;
  const envelope = ctx.createGain();
  // A 10 ms rise (no click), then an exponential fade, like a struck bell.
  envelope.gain.setValueAtTime(0.0001, at);
  envelope.gain.exponentialRampToValueAtTime(peak, at + 0.01);
  envelope.gain.exponentialRampToValueAtTime(0.0001, at + length);
  oscillator.connect(envelope).connect(out);
  oscillator.start(at);
  oscillator.stop(at + length);
}

function chime(at: number) {
  // A tab that cannot make a sound leaves the chime to one that can.
  if (!soundOn() || !mayPlay()) return;
  // Another tab of this app may already have played this one.
  if (Number(localStorage.getItem(HEARD_KEY)) >= at) return;
  localStorage.setItem(HEARD_KEY, String(at));
  void playChime();
}

/**
 * Chimes when the newest unread notification is newer than any this page has
 * seen. `latestAt` is undefined until the bell has asked the server since the
 * page opened; the first answer only sets the mark, so what was already
 * waiting stays quiet.
 */
export function useNotificationChime(latestAt: string | null | undefined): void {
  const seen = useRef<number | null>(null);

  useEffect(() => {
    if (latestAt === undefined) return;
    const at = latestAt ? Date.parse(latestAt) : 0;
    const before = seen.current;
    if (before !== null && at <= before) return;
    seen.current = at;
    if (before !== null) chime(at);
  }, [latestAt]);

  // Safari lets a page make sound only once it has started its audio inside a
  // click or a key press, so the first one wakes it. Elsewhere this is harmless.
  useEffect(() => {
    const wake = () => {
      stop();
      const ctx = soundOn() ? audio() : null;
      if (ctx) void ctx.resume().then(() => release(ctx), () => undefined);
    };
    const stop = () => {
      window.removeEventListener('pointerdown', wake, true);
      window.removeEventListener('keydown', wake, true);
    };
    window.addEventListener('pointerdown', wake, true);
    window.addEventListener('keydown', wake, true);
    return stop;
  }, []);
}

/** The bell's mute switch, kept per browser and in step across its tabs. */
export function useNotificationSound(): [boolean, (on: boolean) => void] {
  const [on, setOn] = useState(soundOn);

  useEffect(() => {
    const sync = (e: StorageEvent) => {
      if (e.key === MUTED_KEY || e.key === null) setOn(soundOn());
    };
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, []);

  const set = useCallback((next: boolean) => {
    if (next) localStorage.removeItem(MUTED_KEY);
    else localStorage.setItem(MUTED_KEY, 'off');
    setOn(next);
    // Turning it on plays it once, so the person hears what to listen for.
    if (next) void playChime();
  }, []);

  return [on, set];
}
